import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { API, getToken } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { SECTIONS } from "../lib/fields";
import { toast } from "sonner";

/**
 * Read-only detail view of a single Certificate of Sponsorship.
 * Layout follows the SMS portal "CoS Details" pattern: sectioned panels with
 * label / value rows, action buttons at the bottom.
 */
export default function CosView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cos, setCos] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/cos/${id}`);
        setCos(data);
      } catch {
        toast.error("Failed to load certificate");
        navigate("/cos");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const download = async () => {
    if (!cos) return;
    try {
      const res = await fetch(`${API}/cos/${cos.id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = cos.certificate_number ? `CoS-${cos.certificate_number}.pdf` : `CoS.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download");
    }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/cos/${cos.id}/duplicate`);
      toast.success("Duplicated");
      navigate(`/cos/${data.id}/view`);
    } catch {
      toast.error("Failed to duplicate");
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this certificate? This cannot be undone.")) return;
    try {
      await api.delete(`/cos/${cos.id}`);
      toast.success("Deleted");
      navigate("/cos");
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <AppLayout breadcrumb={[{ label: "View CoS", to: "/cos" }, { label: "Loading" }]}>
        <p>Loading…</p>
      </AppLayout>
    );
  }
  if (!cos) return null;

  const displayValue = (val) => {
    if (val === null || val === undefined || val === "") return <span style={{ color: "#888" }}>—</span>;
    return String(val);
  };

  const statusClass = () => {
    const k = (cos.current_certificate_status || "").toLowerCase();
    if (k.includes("ready")) return "ready";
    if (k.includes("assign")) return "assigned";
    if (k.includes("used")) return "used";
    return "";
  };

  return (
    <AppLayout
      activeMenu="view"
      breadcrumb={[
        { label: "Sponsorship management system", to: "/" },
        { label: "Workers", to: "/cos" },
        { label: "View CoS", to: "/cos" },
        { label: "CoS details" },
      ]}
    >
      <h1 data-testid="cos-view-heading">CoS details</h1>
      <p>
        The certificate details below are shown as recorded. Choose{" "}
        <strong>Edit</strong> to make changes, <strong>PDF</strong> to download,{" "}
        <strong>Duplicate</strong> to copy this certificate, or{" "}
        <strong>Back</strong> to return to the results list.
      </p>

      <div className="sms-panel" data-testid="summary-panel">
        <div className="sms-panel-title">Summary</div>
        <div className="sms-panel-body">
          <div className="sms-field-row">
            <span className="sms-field-label">Certificate number:</span>
            <strong style={{ fontFamily: "monospace" }}>{displayValue(cos.certificate_number)}</strong>
          </div>
          <div className="sms-field-row">
            <span className="sms-field-label">Current status:</span>
            <span>
              <span className={`sms-status-pill ${statusClass()}`}>
                {cos.current_certificate_status || "—"}
              </span>
            </span>
          </div>
          <div className="sms-field-row">
            <span className="sms-field-label">Migrant:</span>
            <span>{[cos.given_names, cos.family_name].filter(Boolean).join(" ") || "—"}</span>
          </div>
          <div className="sms-field-row">
            <span className="sms-field-label">Sponsor:</span>
            <span>{cos.sponsor_name || "—"}</span>
          </div>
        </div>
      </div>

      {SECTIONS.map((sec) => (
        <div key={sec.id} className="sms-panel" data-testid={`view-section-${sec.id}`}>
          <div className="sms-panel-title">{sec.title}</div>
          <div className="sms-panel-body">
            {sec.fields.map((f) => (
              <div key={f.name} className="sms-field-row">
                <span className="sms-field-label">{f.label}:</span>
                <span data-testid={`view-field-${f.name}`} style={{ paddingTop: 6, whiteSpace: "pre-wrap" }}>
                  {displayValue(cos[f.name])}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="sms-actionbar">
        <div className="sms-actionbar-left">
          <Link to="/cos" className="sms-btn secondary" data-testid="view-back-btn">
            Back
          </Link>
          <button
            type="button" onClick={del} data-testid="view-delete-btn"
            className="sms-btn secondary" style={{ color: "#8f1414" }}
          >
            Delete
          </button>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button" onClick={duplicate} data-testid="view-duplicate-btn"
            className="sms-btn secondary"
          >
            Duplicate
          </button>
          <button
            type="button" onClick={() => navigate(`/cos/${cos.id}`)}
            data-testid="view-edit-btn" className="sms-btn secondary"
          >
            Edit
          </button>
          <button
            type="button" onClick={download} data-testid="view-pdf-btn" className="sms-btn"
          >
            Download PDF
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
